import type {DataRouterApplicationBuilder} from '@github-ui/react-core/future/data-router-application'
import type {RouteObject} from '@github-ui/react-router'
import type {ComponentType} from 'react'

import {workflowRouteParam} from '../workflow-path'
import {buildAllWorkflowRunsRoute} from './all-workflow-runs-route'
import {buildWorkflowRunsRoute} from './workflow-runs-route'
import type {ActionsShellRouteHandle} from './actions-shell-route-handle'
import {buildWorkflowsLayoutRoute} from './workflows-layout-route'
import {createActionsRailsFragmentRoutes, ActionsRailsFragmentErrorBoundary} from './actions-rails-fragment-routes'
import {buildActionsCachesRoute} from './actions-caches-route'

export interface ActionsWorkflowsRouteComponents {
  AllWorkflowRuns: ComponentType
  AllWorkflowRunsError: ComponentType
  RepositoryRunners: ComponentType
  WorkflowRuns: ComponentType
  WorkflowRunsError: ComponentType
  WorkflowsLayout: ComponentType
}

const allWorkflowsShell = {
  kind: 'workflow',
  title: 'All workflows',
  getSelection: () => ({type: 'all-workflows'}),
} satisfies ActionsShellRouteHandle

const workflowRunsShell = {
  kind: 'workflow',
  title: 'Workflow runs',
  getSelection: params => ({type: 'workflow', filename: workflowRouteParam(params)}),
} satisfies ActionsShellRouteHandle

// The layout renders the native caches header, so the route has no content of its own.
const NativeCachesRoute = () => null

const cachesShell = {
  kind: 'native',
  title: 'Caches',
  getSelection: () => ({type: 'caches'}),
} satisfies ActionsShellRouteHandle

/** Builds the Actions Workflows routes for either the standalone or repo app builder. */
export function createActionsWorkflowsRoutes(
  appBuilder: DataRouterApplicationBuilder<string>,
  components: ActionsWorkflowsRouteComponents,
  {
    nativeCachesEnabled = false,
    nativeRunnersEnabled = false,
  }: {nativeCachesEnabled?: boolean; nativeRunnersEnabled?: boolean} = {},
): RouteObject[] {
  const {AllWorkflowRuns, AllWorkflowRunsError, RepositoryRunners, WorkflowRuns, WorkflowRunsError, WorkflowsLayout} =
    components
  const cachesRoute = buildActionsCachesRoute(appBuilder)
  const CachesErrorBoundary = () => <ActionsRailsFragmentErrorBoundary appName={appBuilder.name} />

  return [
    buildWorkflowsLayoutRoute(appBuilder).toRoute({
      Component: WorkflowsLayout,
      children: [
        buildAllWorkflowRunsRoute(appBuilder).toRoute({
          Component: AllWorkflowRuns,
          handle: {actionsShell: allWorkflowsShell},
          errorBoundary: {fallback: <AllWorkflowRunsError />},
        }),
        buildWorkflowRunsRoute(appBuilder).toRoute({
          Component: WorkflowRuns,
          handle: {actionsShell: workflowRunsShell},
          errorBoundary: {fallback: <WorkflowRunsError />},
        }),
        ...(nativeCachesEnabled
          ? [
              cachesRoute.toRoute({
                Component: NativeCachesRoute,
                handle: {actionsShell: cachesShell},
                errorBoundary: {override: CachesErrorBoundary},
              }),
            ]
          : []),
        ...createActionsRailsFragmentRoutes(appBuilder, {nativeCachesEnabled, nativeRunnersEnabled, RepositoryRunners}),
      ],
    }),
  ]
}
